import { useCallback, useEffect, useRef, useState } from 'react'

import { APP_VERSION } from '@/shared/config/appVersion'

import { fetchDeploymentVersion, type VersionFetcher } from './versionCheck'

export interface VersionUpdateOptions {
  bundledVersion?: string
  fetcher?: VersionFetcher
  reload?: () => void
  visibilityDocument?: Document
}

export function reloadApplication() {
  window.location.reload()
}

export function useVersionUpdate({
  bundledVersion = APP_VERSION,
  fetcher = fetch,
  reload = reloadApplication,
  visibilityDocument = document,
}: VersionUpdateOptions = {}) {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const checkingRef = useRef(false)
  const updateAvailableRef = useRef(false)

  const checkVersion = useCallback(async () => {
    if (checkingRef.current || updateAvailableRef.current) return

    checkingRef.current = true

    try {
      const deployedVersion = await fetchDeploymentVersion(fetcher)

      if (deployedVersion !== bundledVersion) {
        updateAvailableRef.current = true
        setUpdateAvailable(true)
      }
    } catch {
      // Version detection is best-effort and must never block the application.
    } finally {
      checkingRef.current = false
    }
  }, [bundledVersion, fetcher])

  useEffect(() => {
    queueMicrotask(() => void checkVersion())

    const handleVisibilityChange = () => {
      if (visibilityDocument.visibilityState === 'visible') {
        void checkVersion()
      }
    }

    visibilityDocument.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      visibilityDocument.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [checkVersion, visibilityDocument])

  return { updateAvailable, reload }
}
